import { Add, Remove } from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  List,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import ActionConfirmDialog from '~/components/Commons/ActionConfirmDialog';
import DashboardContainer from '~/components/Commons/DashboardContainer';
import DianomiSpinner from '~/components/Commons/DianomiSpinner/DianomiSpinner';
import StyledAccordion from '~/components/Commons/ExpansionPanel';
import CustomLink from '~/components/Commons/Link/Link';
import useNotifications from '~/context/Notifications';
import {
  buildScopedRealignmentApplyBody,
  getChildLimitDriftDetails,
  getExcessCatalogIdsFromDriftDetails,
  toStatAccessErrorInput,
  type ChildLimitExceedsParentCeilingDetail,
} from '~/hooks/analyticsStatAccess/statAccessErrorUtils';
import { useApplyStaffStatColumnCatalogMutation } from '~/hooks/mutations/useApplyStaffStatColumnCatalogMutation';
import useSessionPlQuery from '~/hooks/queries/useSessionPlQuery';
import { useStaffQuery } from '~/hooks/queries/useStaffQuery/useStaffQuery';
import {
  useStaffStatColumnCatalogQuery,
  type StaffStatCatalogRow,
} from '~/hooks/queries/useStaffStatColumnCatalogQuery';
import type { StaffStatColumnCatalogDivergence } from '~/hooks/queries/useStaffStatColumnCatalogQuery/staffStatColumnCatalogSchema';
import { useStaffStatsAccessQuery } from '~/hooks/queries/useStaffStatsAccessQuery';
import type { AnalyticsReportingMode, Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import { getAnalyticsReportingMode, isGenie2ReportingMode } from '~/utils/analyticsReportingMode';
import {
  ErrorStateCard,
  NoSearchMatchCard,
} from '~/views/Dashboard/AnalyticsStats/AnalyticsStatsShared';
import {
  ColumnPickerListItem,
  ColumnPickerToggleAllButton,
} from '~/views/Dashboard/AnalyticsStats/ColumnPickerListItem';
import {
  sortTagHeaders,
  tagHeaderSx,
} from '~/views/Dashboard/AnalyticsStats/analyticsStatsColumnListUtils';
import {
  AnalyticsStatsApiError,
  getAnalyticsStatsErrorMessage,
} from '~/views/Dashboard/AnalyticsStats/analyticsStatsErrorUtils';

import { ReportAccessLossWarning } from './ReportAccessLossWarning';
import { ColumnAccessDiffSection, ColumnAccessGoLiveWarning } from './columnAccessApplyConfirm';
import {
  diffColumnAccessSelection,
  shouldConfirmParentColumnAccessGoLive,
} from './columnAccessApplyConfirmUtils';
import { useStaffAnalyticsConfigMode } from './StaffAnalyticsConfigModeContext';
import { useStatAccessMode } from './StatAccessModeContext';
import {
  buildDimensionBundles,
  collectCatalogIdsByTagWithParentDimensions,
  collectCatalogIdsFromDimensionBundles,
  filterCatalogForStaffSearch,
  groupCatalogRowsByPrimaryTag,
  standaloneDisplayRows,
  statRowsInCatalog,
  type CatalogTagItem,
} from './staffCatalogColumnLayoutUtils';

const EMPTY_CATALOG: StaffStatCatalogRow[] = [];

const sectionHeaderButtonSx = {
  py: 1,
  px: 2,
  borderLeft: '4px solid',
  borderColor: 'dianomiGreen.main',
  bgcolor: 'grey.100',
  borderRadius: '0 2px 2px 0',
  width: '100%',
} as const;

const accordionSlotProps = {
  details: { sx: { maxHeight: '40vh', overflowY: 'auto' as const } },
};

function rowInitiallyChecked(row: StaffStatCatalogRow): boolean {
  return Object.values(row.effectiveAllowByStatKey).some((v) => v === true);
}

function buildAllowSetFromCatalog(catalog: StaffStatCatalogRow[]): Set<string> {
  return new Set(catalog.filter(rowInitiallyChecked).map((r) => r.catalogId));
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

function statKeysFromDivergence(divergence: StaffStatColumnCatalogDivergence | null): string[] {
  if (!divergence) return [];
  const keys = new Set<string>();
  for (const detail of divergence.details) {
    Object.keys(detail.byStatKey).forEach((k) => keys.add(k));
  }
  return [...keys];
}

type AccordionSection = 'dimensions' | 'metrics' | 'details';

type TagQuickActionColumn = {
  catalogId: string;
  label: string;
};

type TagQuickAction = {
  tag: string;
  ids: string[];
  columns: TagQuickActionColumn[];
  totalCount: number;
  selectedCount: number;
  allSelected: boolean;
};

function TagQuickActionTooltipTitle({ action, add }: { action: TagQuickAction; add: boolean }) {
  const columnWord = action.columns.length === 1 ? 'column' : 'columns';
  return (
    <Box sx={{ maxWidth: 360, maxHeight: 280, overflowY: 'auto' }}>
      <Typography variant="caption" component="div" fontWeight={600} sx={{ mb: 0.5 }}>
        {add ? 'Allow' : 'Remove'} {action.tag} ({action.columns.length} {columnWord})
      </Typography>
      <Typography variant="caption" component="div" color="grey.300" sx={{ mb: 0.75 }}>
        {action.selectedCount}/{action.totalCount} currently allowed
      </Typography>
      <Stack component="ul" spacing={0.25} sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {action.columns.map(({ catalogId, label }) => (
          <Typography
            key={catalogId}
            variant="caption"
            component="li"
            sx={{ lineHeight: 1.35, wordBreak: 'break-word' }}
          >
            {label}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

type TaggedCatalogBlockProps = {
  groups: Map<string, CatalogTagItem[]>;
  headers: string[];
  untagged: StaffStatCatalogRow[];
  selectedCatalogIds: Set<string>;
  bulkBlocked: boolean;
  onToggleId: (catalogId: string, checked: boolean) => void;
  onAddRemoveGroup: (items: CatalogTagItem[] | StaffStatCatalogRow[], add: boolean) => void;
  headerSuffix?: string;
};

function StaffCatalogTaggedGroups({
  groups,
  headers,
  untagged,
  selectedCatalogIds,
  bulkBlocked,
  onToggleId,
  onAddRemoveGroup,
  headerSuffix = '',
}: TaggedCatalogBlockProps) {
  const renderGroup = (header: string, groupItems: CatalogTagItem[]) => {
    const ids = groupItems.map((i) => i.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedCatalogIds.has(id));

    return (
      <Box key={header} sx={{ mb: 1.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={0.5}
          sx={{ mb: 0.5, width: '100%', ...tagHeaderSx }}
        >
          <Typography variant="caption" fontWeight={600}>
            {header}
            {headerSuffix}
          </Typography>
          <ColumnPickerToggleAllButton
            allSelected={allSelected}
            disabled={bulkBlocked || groupItems.length === 0}
            onClick={() => onAddRemoveGroup(groupItems, !allSelected)}
          />
        </Stack>
        <List disablePadding dense>
          {groupItems.map((item) => (
            <ColumnPickerListItem
              key={item.id}
              label={item.label}
              tooltip={item.tooltip}
              selected={selectedCatalogIds.has(item.id)}
              onToggle={() => onToggleId(item.id, !selectedCatalogIds.has(item.id))}
              disabled={bulkBlocked}
            />
          ))}
        </List>
      </Box>
    );
  };

  return (
    <>
      {headers.map((header) => renderGroup(header, groups.get(header) ?? []))}
      {untagged.length > 0 ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            fontWeight={600}
            component="div"
            sx={{ mb: 0.5, width: '100%', ...tagHeaderSx }}
          >
            Other
          </Typography>
          <List disablePadding dense>
            {untagged.map((row) => (
              <ColumnPickerListItem
                key={row.catalogId}
                label={row.label}
                tooltip={row.tooltip}
                selected={selectedCatalogIds.has(row.catalogId)}
                onToggle={() => onToggleId(row.catalogId, !selectedCatalogIds.has(row.catalogId))}
                disabled={bulkBlocked}
              />
            ))}
          </List>
        </Box>
      ) : null}
    </>
  );
}

export default function StaffUnifiedColumnCatalogPage() {
  const statAccessMode = useStatAccessMode();
  const isParentMode = statAccessMode === 'parent';
  const { accountType, accountId, childClientId } = useParams<{
    accountType: string;
    accountId: string;
    childClientId?: string;
  }>();
  let accountTypeValid: 'client' | 'partner' = 'client';
  if (!isParentMode && accountType === 'partner') {
    accountTypeValid = 'partner';
  }

  /** Parent hub keeps parent id in the URL; API target is the child. */
  const targetAccountId = isParentMode ? childClientId ?? '' : accountId ?? '';
  const parentAccountId = isParentMode ? accountId ?? null : null;
  const { configQuery, writeAnalyticsReportingMode, persistedMode } = useStaffAnalyticsConfigMode();
  const { data: sessionData } = useSessionPlQuery();
  const parentSessionMode = getAnalyticsReportingMode(sessionData);
  const parentGenie2Mode =
    isParentMode && isGenie2ReportingMode(parentSessionMode) ? parentSessionMode : null;

  const parentScope = {
    ...(parentAccountId ? { parentAccountId } : {}),
    analyticsReportingMode: configQuery?.analyticsReportingMode ?? null,
  };
  const queryAnalyticsReportingMode = configQuery?.analyticsReportingMode ?? null;

  const catalogQuery = useStaffStatColumnCatalogQuery(
    accountTypeValid,
    targetAccountId,
    parentScope,
  );
  const accessQuery = useStaffStatsAccessQuery(accountTypeValid, targetAccountId, parentScope);
  const staffQuery = useStaffQuery({ enabled: !isParentMode });
  const isStaffAdmin = !isParentMode && !!staffQuery.data?.permissions.admin;

  /** Unscoped persisted mode from the shell — do not use the pre-seed access GET. */
  const childPersistedMode = persistedMode;
  const confirmGoLiveOnApply = isParentMode
    ? shouldConfirmParentColumnAccessGoLive({
        parentGenie2Mode,
        childPersistedMode,
      })
    : configQuery != null;
  /** Staff: body assigns chosen/live mode. Parent: API ignores body and uses parent mode. */
  const bodyAnalyticsReportingMode = isParentMode ? null : writeAnalyticsReportingMode;
  /** Mode shown in the go-live confirm (staff write mode, or parent Genie 2.0 mode). */
  const goLiveReportingMode = isParentMode ? parentGenie2Mode : writeAnalyticsReportingMode;

  const constraintAccountId =
    catalogQuery.data?.constraintAccountId ?? accessQuery.data?.constraintAccountId;

  const statNameByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of accessQuery.data?.stats ?? []) {
      map.set(s.statKey, s.statName);
    }
    return map;
  }, [accessQuery.data?.stats]);

  const catalog = catalogQuery.data?.catalog ?? EMPTY_CATALOG;
  const allStatsUnconfigured =
    isParentMode &&
    (accessQuery.data?.stats ?? []).length > 0 &&
    (accessQuery.data?.stats ?? []).every((stat) => !stat.hasLimit);

  if (!targetAccountId || (isParentMode && !parentAccountId)) {
    const invalid = <Box sx={{ p: 2 }}>Invalid account.</Box>;
    return isParentMode ? invalid : <DashboardContainer>{invalid}</DashboardContainer>;
  }

  if (catalogQuery.isError) {
    const error = (
      <Box sx={{ py: 2 }}>
        <ErrorStateCard />
      </Box>
    );
    return isParentMode ? error : <DashboardContainer>{error}</DashboardContainer>;
  }

  return (
    <StaffUnifiedColumnCatalogWorkspace
      key={catalogQuery.dataUpdatedAt}
      statAccessMode={statAccessMode}
      accountTypeValid={accountTypeValid}
      accountId={targetAccountId}
      parentAccountId={parentAccountId}
      analyticsReportingMode={bodyAnalyticsReportingMode}
      goLiveReportingMode={goLiveReportingMode}
      queryAnalyticsReportingMode={queryAnalyticsReportingMode}
      confirmGoLiveOnApply={confirmGoLiveOnApply}
      currentReportingMode={childPersistedMode}
      catalog={catalog}
      allStatKeys={(accessQuery.data?.stats ?? []).map((stat) => stat.statKey)}
      bulkEditable={catalogQuery.data?.bulkEditable ?? false}
      divergence={catalogQuery.data?.divergence ?? null}
      statColumnTagsSortOrder={catalogQuery.data?.statColumnTagsSortOrder}
      displayColumnTagsSortOrder={catalogQuery.data?.displayColumnTagsSortOrder}
      catalogLoading={catalogQuery.isLoading}
      accessLoading={accessQuery.isLoading}
      isStaffAdmin={isStaffAdmin}
      statNameByKey={statNameByKey}
      constraintAccountId={constraintAccountId}
      allStatsUnconfigured={allStatsUnconfigured}
    />
  );
}

type StaffUnifiedColumnCatalogWorkspaceProps = {
  statAccessMode: 'staff' | 'parent';
  accountTypeValid: 'client' | 'partner';
  accountId: string;
  /** Parent hub only — session/path parent id for children-mount APIs. */
  parentAccountId?: string | null;
  /**
   * Staff write body mode (live or pre-seed). Parent omits (API ignores body and
   * always persists the parent's Genie 2.0 mode).
   */
  analyticsReportingMode?: Genie2ReportingMode | null;
  /** Mode shown in the go-live confirm dialog. */
  goLiveReportingMode?: Genie2ReportingMode | null;
  /** Pre-seed query param only; omit when live. */
  queryAnalyticsReportingMode?: Genie2ReportingMode | null;
  /** Classic Genie go-live / parent mode switch: confirm before Apply. */
  confirmGoLiveOnApply?: boolean;
  /** Account/child persisted mode before this write (for confirm copy). */
  currentReportingMode?: AnalyticsReportingMode | null;
  catalog: StaffStatCatalogRow[];
  allStatKeys: string[];
  bulkEditable: boolean;
  divergence: StaffStatColumnCatalogDivergence | null;
  statColumnTagsSortOrder: string[] | undefined;
  displayColumnTagsSortOrder: string[] | undefined;
  catalogLoading: boolean;
  accessLoading: boolean;
  isStaffAdmin: boolean;
  statNameByKey: Map<string, string>;
  constraintAccountId?: number;
  allStatsUnconfigured: boolean;
};

/** Remount when catalog data refreshes so draft selection resets without useEffect. */
export function StaffUnifiedColumnCatalogWorkspace({
  statAccessMode,
  accountTypeValid,
  accountId,
  parentAccountId = null,
  analyticsReportingMode = null,
  goLiveReportingMode = null,
  queryAnalyticsReportingMode = null,
  confirmGoLiveOnApply = false,
  currentReportingMode = null,
  catalog,
  allStatKeys,
  bulkEditable,
  divergence,
  statColumnTagsSortOrder,
  displayColumnTagsSortOrder,
  catalogLoading,
  accessLoading,
  isStaffAdmin,
  statNameByKey,
  constraintAccountId,
  allStatsUnconfigured,
}: StaffUnifiedColumnCatalogWorkspaceProps) {
  const isParentMode = statAccessMode === 'parent';
  // Partners skip the Genie 2.0 universe chooser; Classic Genie partners write without a body mode.
  const canSubmitColumnAccess = isParentMode
    ? goLiveReportingMode != null
    : accountTypeValid === 'partner' || analyticsReportingMode != null;
  const { setTimedNotification } = useNotifications();
  const applyMutation = useApplyStaffStatColumnCatalogMutation();

  const [selectedCatalogIds, setSelectedCatalogIds] = useState<Set<string>>(
    () => new Set(buildAllowSetFromCatalog(catalog)),
  );
  const [search, setSearch] = useState('');
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<AccordionSection | null>('dimensions');
  const [ceilingDriftDetails, setCeilingDriftDetails] = useState<
    ChildLimitExceedsParentCeilingDetail[]
  >([]);

  const ceilingDriftCatalogIds = useMemo(
    () => getExcessCatalogIdsFromDriftDetails(ceilingDriftDetails),
    [ceilingDriftDetails],
  );

  const serverAllowSet = useMemo(() => buildAllowSetFromCatalog(catalog), [catalog]);

  const isDirty = useMemo(
    () => !setsEqual(selectedCatalogIds, serverAllowSet),
    [selectedCatalogIds, serverAllowSet],
  );

  const bulkBlocked = isParentMode ? !bulkEditable : !bulkEditable || divergence != null;

  const handleApplyError = useCallback(
    (err: unknown) => {
      if (isParentMode && err instanceof AnalyticsStatsApiError) {
        const driftDetails = getChildLimitDriftDetails(toStatAccessErrorInput(err.rawResponse));
        if (driftDetails.length) {
          setCeilingDriftDetails(driftDetails);
        }
      }
      setTimedNotification(
        'danger',
        getAnalyticsStatsErrorMessage(err, {
          fallbackMessage: 'Could not apply column catalog. Please try again.',
        }),
        8000,
      );
    },
    [isParentMode, setTimedNotification],
  );

  const realignCeilingDrift = useCallback(() => {
    if (!accountId || ceilingDriftDetails.length === 0 || !canSubmitColumnAccess) return;
    const body = buildScopedRealignmentApplyBody(
      catalog,
      ceilingDriftDetails,
      analyticsReportingMode ?? undefined,
    );
    if (!body) return;

    applyMutation.mutate(
      {
        accountType: accountTypeValid,
        accountId,
        body,
        asStaff: false,
        parentAccountId,
        queryAnalyticsReportingMode,
      },
      {
        onSuccess: (res) => {
          setCeilingDriftDetails([]);
          setTimedNotification(
            'success',
            `Removed columns above the parent ceiling${
              res.touchedStatKeys.length ? ` (${res.touchedStatKeys.length} stats)` : ''
            }. You can now apply other changes.`,
            8000,
          );
        },
        onError: handleApplyError,
      },
    );
  }, [
    accountId,
    accountTypeValid,
    analyticsReportingMode,
    applyMutation,
    canSubmitColumnAccess,
    catalog,
    ceilingDriftDetails,
    handleApplyError,
    parentAccountId,
    queryAnalyticsReportingMode,
    setTimedNotification,
  ]);

  const filteredCatalog = useMemo(
    () => filterCatalogForStaffSearch(catalog, search),
    [catalog, search],
  );

  const dimensionBundles = useMemo(() => buildDimensionBundles(filteredCatalog), [filteredCatalog]);
  const statRows = useMemo(() => statRowsInCatalog(filteredCatalog), [filteredCatalog]);
  const standaloneDisplays = useMemo(
    () => standaloneDisplayRows(filteredCatalog),
    [filteredCatalog],
  );

  const statTagData = useMemo(
    () => groupCatalogRowsByPrimaryTag(statRows, statColumnTagsSortOrder),
    [statRows, statColumnTagsSortOrder],
  );

  const detailsTagData = useMemo(
    () => groupCatalogRowsByPrimaryTag(standaloneDisplays, displayColumnTagsSortOrder),
    [standaloneDisplays, displayColumnTagsSortOrder],
  );

  const handleAccordionChange = (section: AccordionSection) => (nextExpanded: boolean) => {
    setExpandedSection(nextExpanded ? section : null);
  };

  const toggleCatalogId = useCallback((catalogId: string, checked: boolean) => {
    setSelectedCatalogIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(catalogId);
      else next.delete(catalogId);
      return next;
    });
  }, []);

  const toggleDimensionRow = useCallback(
    (dimension: StaffStatCatalogRow, checked: boolean, attributes: StaffStatCatalogRow[]) => {
      setSelectedCatalogIds((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(dimension.catalogId);
        } else {
          next.delete(dimension.catalogId);
          for (const a of attributes) {
            next.delete(a.catalogId);
          }
        }
        return next;
      });
    },
    [],
  );

  const toggleTagGroup = useCallback(
    (items: CatalogTagItem[] | StaffStatCatalogRow[], add: boolean) => {
      const ids =
        items.length && 'row' in items[0]
          ? (items as CatalogTagItem[]).map((i) => i.id)
          : (items as StaffStatCatalogRow[]).map((r) => r.catalogId);
      setSelectedCatalogIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => {
          if (add) next.add(id);
          else next.delete(id);
        });
        return next;
      });
    },
    [],
  );

  const selectAllCatalog = useCallback(() => {
    setSelectedCatalogIds((prev) => {
      const next = new Set(prev);
      catalog.forEach((row) => next.add(row.catalogId));
      return next;
    });
  }, [catalog]);

  const dimensionSectionIds = useMemo(
    () => collectCatalogIdsFromDimensionBundles(dimensionBundles),
    [dimensionBundles],
  );
  const metricSectionIds = useMemo(() => statRows.map((r) => r.catalogId), [statRows]);
  const detailsSectionIds = useMemo(
    () => standaloneDisplays.map((r) => r.catalogId),
    [standaloneDisplays],
  );

  const toggleCatalogSectionIds = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setSelectedCatalogIds((prev) => {
      const allSelected = ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  const allDimensionsInSectionSelected =
    dimensionSectionIds.length > 0 && dimensionSectionIds.every((id) => selectedCatalogIds.has(id));
  const allMetricsInSectionSelected =
    metricSectionIds.length > 0 && metricSectionIds.every((id) => selectedCatalogIds.has(id));
  const allDetailsInSectionSelected =
    detailsSectionIds.length > 0 && detailsSectionIds.every((id) => selectedCatalogIds.has(id));

  const catalogLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of catalog) {
      map.set(row.catalogId, row.label);
    }
    return map;
  }, [catalog]);

  const applySelectionDiff = useMemo(
    () => diffColumnAccessSelection(selectedCatalogIds, serverAllowSet, catalogLabelById),
    [catalogLabelById, selectedCatalogIds, serverAllowSet],
  );

  const tagQuickActions = useMemo<TagQuickAction[]>(() => {
    const idsByTag = collectCatalogIdsByTagWithParentDimensions(catalog);

    const tagSortOrder = [
      ...new Set([...(statColumnTagsSortOrder ?? []), ...(displayColumnTagsSortOrder ?? [])]),
    ];

    return sortTagHeaders([...idsByTag.keys()], tagSortOrder).map((tag) => {
      const ids = idsByTag.get(tag) ?? [];
      const selectedCount = ids.filter((id) => selectedCatalogIds.has(id)).length;
      const columns = ids
        .map((catalogId) => ({
          catalogId,
          label: catalogLabelById.get(catalogId) ?? catalogId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      return {
        tag,
        ids,
        columns,
        totalCount: ids.length,
        selectedCount,
        allSelected: ids.length > 0 && selectedCount === ids.length,
      };
    });
  }, [
    catalog,
    catalogLabelById,
    displayColumnTagsSortOrder,
    selectedCatalogIds,
    statColumnTagsSortOrder,
  ]);

  const toggleTagQuickAction = useCallback((ids: string[], add: boolean) => {
    setSelectedCatalogIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (add) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, []);

  const handleApply = useCallback(() => {
    if (!accountId || !canSubmitColumnAccess) return;
    applyMutation.mutate(
      {
        accountType: accountTypeValid,
        accountId,
        body: {
          allowedCatalogIds: [...selectedCatalogIds],
          ...(analyticsReportingMode != null ? { analyticsReportingMode } : {}),
        },
        asStaff: !isParentMode,
        parentAccountId,
        queryAnalyticsReportingMode,
      },
      {
        onSuccess: (res) => {
          setApplyDialogOpen(false);
          setCeilingDriftDetails([]);
          setTimedNotification(
            'success',
            `Column access updated${
              res.touchedStatKeys.length ? ` (${res.touchedStatKeys.length} stats)` : ''
            }.`,
            5000,
          );
        },
        onError: handleApplyError,
      },
    );
  }, [
    accountId,
    accountTypeValid,
    analyticsReportingMode,
    applyMutation,
    canSubmitColumnAccess,
    handleApplyError,
    isParentMode,
    parentAccountId,
    queryAnalyticsReportingMode,
    selectedCatalogIds,
    setTimedNotification,
  ]);

  const handleApplyClick = useCallback(() => {
    if (confirmGoLiveOnApply) {
      setApplyDialogOpen(true);
      return;
    }
    handleApply();
  }, [confirmGoLiveOnApply, handleApply]);

  const handleConfirmClearAll = useCallback(() => {
    if (!accountId || allStatKeys.length === 0 || !canSubmitColumnAccess) return;
    applyMutation.mutate(
      {
        accountType: accountTypeValid,
        accountId,
        body: {
          allowedCatalogIds: [],
          statKeys: allStatKeys,
          ...(analyticsReportingMode != null ? { analyticsReportingMode } : {}),
        },
        asStaff: !isParentMode,
        parentAccountId,
        queryAnalyticsReportingMode,
      },
      {
        onSuccess: () => {
          setClearDialogOpen(false);
          setTimedNotification('success', 'All column limits removed for this account.', 5000);
        },
        onError: handleApplyError,
      },
    );
  }, [
    accountId,
    accountTypeValid,
    allStatKeys,
    analyticsReportingMode,
    applyMutation,
    canSubmitColumnAccess,
    handleApplyError,
    isParentMode,
    parentAccountId,
    queryAnalyticsReportingMode,
    setTimedNotification,
  ]);

  const statSetSearch = queryAnalyticsReportingMode
    ? `?analyticsReportingMode=${queryAnalyticsReportingMode}`
    : '';
  const individualStatsPath = isParentMode
    ? null
    : `/staff/${accountTypeValid}/${
        accountId ?? ''
      }/analytics-access/individual-stats${statSetSearch}`;

  const divergenceStatKeys = useMemo(() => statKeysFromDivergence(divergence), [divergence]);

  const searchActiveNoResults =
    Boolean(search.trim()) && filteredCatalog.length === 0 && catalog.length > 0;

  const hasStructuredRows =
    dimensionBundles.length > 0 || statRows.length > 0 || standaloneDisplays.length > 0;

  const isInitialConfigLoad =
    (catalogLoading || accessLoading) && catalog.length === 0 && allStatKeys.length === 0;

  const body = (
    <>
      <DianomiSpinner
        loading={catalogLoading || accessLoading}
        delay={isInitialConfigLoad ? 0 : undefined}
        minimumTime={isInitialConfigLoad ? 0 : undefined}
      >
        <Stack spacing={3} sx={{ py: 2 }}>
          {!isParentMode ? (
            <Box sx={sectionHeaderButtonSx}>
              <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                All stats column access
              </Typography>
            </Box>
          ) : null}
          {isParentMode ? (
            <Alert severity="info" sx={{ alignSelf: 'stretch' }}>
              Choose which analytics columns this child account can use. Clearing every column that
              belongs only to one report type removes access for that report type entirely. Shared
              columns kept for other report types do not leave a leftover subset for the cleared
              type.
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ alignSelf: 'stretch' }}>
                Use this page to keep column access consistent across all reports.{' '}
                {individualStatsPath ? (
                  <Box
                    component={CustomLink}
                    to={individualStatsPath}
                    sx={{ fontSize: '0.875rem', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Managing individual stats{' '}
                  </Box>
                ) : null}
                can make their rules differ, which disables bulk editing here until those
                differences are resolved.
                {isStaffAdmin ? (
                  <>
                    {' '}
                    <Box
                      component={CustomLink}
                      to="/staff/ops/analytics/column-metadata"
                      sx={{ fontSize: '0.875rem', '&:hover': { textDecoration: 'underline' } }}
                    >
                      Edit column tooltip copy (platform-wide)
                    </Box>
                    .
                  </>
                ) : null}
              </Alert>
              {constraintAccountId != null ? (
                <Alert severity="warning" sx={{ alignSelf: 'stretch' }}>
                  Columns may be limited due to parent account ({constraintAccountId}) restrictions.{' '}
                  <Box
                    component={CustomLink}
                    to={`/staff/client/${constraintAccountId}/analytics-access/columns`}
                    sx={{ fontSize: '0.875rem', '&:hover': { textDecoration: 'underline' } }}
                  >
                    View parent column access
                  </Box>
                </Alert>
              ) : null}
            </>
          )}

          {allStatsUnconfigured && (
            <Alert severity="warning" sx={{ alignSelf: 'stretch' }}>
              This child account has no analytics column access configured yet. Select columns below
              and save to enable analytics reports.
            </Alert>
          )}

          {ceilingDriftCatalogIds.length > 0 && (
            <Alert
              severity="error"
              sx={{ alignSelf: 'stretch' }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={realignCeilingDrift}
                  disabled={applyMutation.isPending}
                >
                  Realign column access
                </Button>
              }
            >
              These columns exceed the parent account ceiling: {ceilingDriftCatalogIds.join(', ')}.
              Realign removes them from this child&apos;s saved access (they are not shown in the
              catalog list), then you can save other changes.
            </Alert>
          )}

          {!isParentMode && bulkBlocked && (
            <Alert severity="warning" sx={{ alignSelf: 'stretch' }}>
              <Typography variant="body2" sx={{ mb: divergence ? 1 : 0 }}>
                {!bulkEditable
                  ? 'Bulk column changes are not available for this account right now.'
                  : 'Policies differ between stats, so bulk apply is turned off until they match.'}
              </Typography>
              {divergence && (
                <>
                  <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                    Conflicting catalog entries:{' '}
                    {divergence.conflictingCatalogIds.join(', ') || '—'}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Adjust these stats individually:
                  </Typography>
                  <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
                    {divergenceStatKeys.map((key) => (
                      <Chip
                        key={key}
                        component={CustomLink}
                        to={`/staff/${accountTypeValid}/${accountId}/analytics-access/${encodeURIComponent(
                          key,
                        )}/limit${statSetSearch}`}
                        label={statNameByKey.get(key) ?? key}
                        clickable
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </>
              )}
            </Alert>
          )}

          {applyMutation.isError && (
            <Alert
              severity="error"
              onClose={() => applyMutation.reset()}
              sx={{ alignSelf: 'stretch' }}
            >
              {getAnalyticsStatsErrorMessage(applyMutation.error, {
                fallbackMessage: 'Request failed. Please try again.',
              })}
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              label="Search columns"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220, maxWidth: 400 }}
              disabled={catalogLoading}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Button
                variant="text"
                size="small"
                onClick={selectAllCatalog}
                disabled={bulkBlocked || catalog.length === 0 || applyMutation.isPending}
              >
                Select all
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleApplyClick}
                disabled={
                  bulkBlocked || !isDirty || applyMutation.isPending || !canSubmitColumnAccess
                }
              >
                Apply changes
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setClearDialogOpen(true)}
                disabled={
                  bulkBlocked ||
                  allStatKeys.length === 0 ||
                  applyMutation.isPending ||
                  !canSubmitColumnAccess
                }
              >
                Remove Access
              </Button>
            </Stack>
          </Stack>

          {tagQuickActions.length > 0 ? (
            <Box
              sx={{
                p: 1.5,
                border: (theme) => theme.defaultStyles.border,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'grey.50',
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                Quick actions
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Toggle common tagged column groups in this draft, then apply changes to save.
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
                {tagQuickActions.map((action) => {
                  const add = !action.allSelected;
                  return (
                    <Tooltip
                      key={action.tag}
                      title={<TagQuickActionTooltipTitle action={action} add={add} />}
                      arrow
                      placement="top"
                      enterDelay={300}
                      slotProps={{
                        tooltip: { sx: { maxWidth: 400 } },
                      }}
                    >
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          color={action.allSelected ? 'error' : 'primary'}
                          startIcon={
                            action.allSelected ? (
                              <Remove sx={{ fontSize: 14 }} />
                            ) : (
                              <Add sx={{ fontSize: 14 }} />
                            )
                          }
                          onClick={() => toggleTagQuickAction(action.ids, add)}
                          disabled={
                            bulkBlocked || applyMutation.isPending || action.totalCount === 0
                          }
                          sx={{ textTransform: 'none' }}
                        >
                          {action.allSelected ? 'Remove' : 'Allow'} {action.tag} (
                          {action.totalCount})
                        </Button>
                      </span>
                    </Tooltip>
                  );
                })}
              </Stack>
            </Box>
          ) : null}

          {searchActiveNoResults ? (
            <NoSearchMatchCard title="No columns match your search" />
          ) : null}

          {!catalogLoading && catalog.length === 0 ? (
            <Alert severity="info">No column catalog is available for this account yet.</Alert>
          ) : null}

          {!searchActiveNoResults && hasStructuredRows ? (
            <Box sx={{ maxWidth: '100%' }}>
              {dimensionBundles.length > 0 ? (
                <StyledAccordion
                  title="Dimensions"
                  alignBody="left"
                  fullWidth
                  expanded={expandedSection === 'dimensions'}
                  onChange={handleAccordionChange('dimensions')}
                  slotProps={accordionSlotProps}
                  summaryAction={
                    <ColumnPickerToggleAllButton
                      allSelected={allDimensionsInSectionSelected}
                      disabled={
                        bulkBlocked || dimensionSectionIds.length === 0 || applyMutation.isPending
                      }
                      stopPropagation
                      addTitle="Add all columns in this section"
                      removeTitle="Remove all columns in this section"
                      onClick={() => toggleCatalogSectionIds(dimensionSectionIds)}
                    />
                  }
                >
                  <List disablePadding dense>
                    {dimensionBundles.map(({ dimension, attributes }) => {
                      const dimSelected = selectedCatalogIds.has(dimension.catalogId);
                      return (
                        <Box key={dimension.catalogId}>
                          <ColumnPickerListItem
                            label={dimension.label}
                            tooltip={dimension.tooltip}
                            selected={dimSelected}
                            onToggle={() => toggleDimensionRow(dimension, !dimSelected, attributes)}
                            disabled={bulkBlocked}
                          />
                          {attributes.length > 0 ? (
                            <Collapse in={dimSelected}>
                              <Box
                                sx={{
                                  ml: 4,
                                  p: 1,
                                  borderRadius: 1,
                                  backgroundColor: 'grey.100',
                                  border: (theme) => theme.defaultStyles.border,
                                  borderColor: 'divider',
                                }}
                              >
                                {(() => {
                                  const nested = groupCatalogRowsByPrimaryTag(
                                    attributes,
                                    displayColumnTagsSortOrder,
                                  );
                                  return (
                                    <StaffCatalogTaggedGroups
                                      groups={nested.groups}
                                      headers={nested.headers}
                                      untagged={nested.untagged}
                                      selectedCatalogIds={selectedCatalogIds}
                                      bulkBlocked={bulkBlocked}
                                      onToggleId={toggleCatalogId}
                                      onAddRemoveGroup={toggleTagGroup}
                                      headerSuffix=" Attributes"
                                    />
                                  );
                                })()}
                              </Box>
                            </Collapse>
                          ) : null}
                        </Box>
                      );
                    })}
                  </List>
                </StyledAccordion>
              ) : null}

              {statRows.length > 0 ? (
                <StyledAccordion
                  title="Metrics"
                  alignBody="left"
                  fullWidth
                  expanded={expandedSection === 'metrics'}
                  onChange={handleAccordionChange('metrics')}
                  slotProps={accordionSlotProps}
                  summaryAction={
                    <ColumnPickerToggleAllButton
                      allSelected={allMetricsInSectionSelected}
                      disabled={
                        bulkBlocked || metricSectionIds.length === 0 || applyMutation.isPending
                      }
                      stopPropagation
                      addTitle="Add all columns in this section"
                      removeTitle="Remove all columns in this section"
                      onClick={() => toggleCatalogSectionIds(metricSectionIds)}
                    />
                  }
                >
                  <StaffCatalogTaggedGroups
                    groups={statTagData.groups}
                    headers={statTagData.headers}
                    untagged={statTagData.untagged}
                    selectedCatalogIds={selectedCatalogIds}
                    bulkBlocked={bulkBlocked}
                    onToggleId={toggleCatalogId}
                    onAddRemoveGroup={toggleTagGroup}
                  />
                </StyledAccordion>
              ) : null}

              {standaloneDisplays.length > 0 ? (
                <StyledAccordion
                  title="Details"
                  alignBody="left"
                  fullWidth
                  expanded={expandedSection === 'details'}
                  onChange={handleAccordionChange('details')}
                  slotProps={accordionSlotProps}
                  summaryAction={
                    <ColumnPickerToggleAllButton
                      allSelected={allDetailsInSectionSelected}
                      disabled={
                        bulkBlocked || detailsSectionIds.length === 0 || applyMutation.isPending
                      }
                      stopPropagation
                      addTitle="Add all columns in this section"
                      removeTitle="Remove all columns in this section"
                      onClick={() => toggleCatalogSectionIds(detailsSectionIds)}
                    />
                  }
                >
                  <StaffCatalogTaggedGroups
                    groups={detailsTagData.groups}
                    headers={detailsTagData.headers}
                    untagged={detailsTagData.untagged}
                    selectedCatalogIds={selectedCatalogIds}
                    bulkBlocked={bulkBlocked}
                    onToggleId={toggleCatalogId}
                    onAddRemoveGroup={toggleTagGroup}
                    headerSuffix=" Attributes"
                  />
                </StyledAccordion>
              ) : null}
            </Box>
          ) : null}
        </Stack>
      </DianomiSpinner>

      <ActionConfirmDialog
        open={applyDialogOpen}
        title="Confirm column access"
        message={
          goLiveReportingMode != null ? (
            <Stack spacing={2}>
              <ColumnAccessDiffSection
                title="Columns to allow"
                labels={applySelectionDiff.addedLabels}
              />
              <ColumnAccessDiffSection
                title="Columns to remove"
                labels={applySelectionDiff.removedLabels}
              />
              <ColumnAccessGoLiveWarning
                mode={goLiveReportingMode}
                audience={isParentMode ? 'parent' : 'staff'}
                currentMode={currentReportingMode}
              />
            </Stack>
          ) : (
            ''
          )
        }
        handleDialogClose={() => {
          if (applyMutation.isPending) return;
          setApplyDialogOpen(false);
        }}
        handleDialogConfirm={handleApply}
        acceptText="Submit"
        isSubmitting={applyMutation.isPending}
      />

      <ActionConfirmDialog
        open={clearDialogOpen}
        title="Remove access"
        message={
          <Stack spacing={2}>
            <ReportAccessLossWarning
              intro="Are you sure you want to remove access for all reports?"
              lossDescription={
                <>
                  The account will no longer be able to access reports or any custom reports based
                  on them including:
                </>
              }
              footer="To restore access, give access again. Any existing custom reports will be preserved but restricted to the columns the new access allows."
            />
            {confirmGoLiveOnApply && goLiveReportingMode != null ? (
              <ColumnAccessGoLiveWarning
                mode={goLiveReportingMode}
                audience={isParentMode ? 'parent' : 'staff'}
                currentMode={currentReportingMode}
              />
            ) : null}
          </Stack>
        }
        handleDialogClose={() => {
          if (applyMutation.isPending) return;
          setClearDialogOpen(false);
        }}
        handleDialogConfirm={handleConfirmClearAll}
        acceptText={confirmGoLiveOnApply ? 'Submit' : 'Remove'}
        isSubmitting={applyMutation.isPending}
      />
    </>
  );

  return isParentMode ? body : <DashboardContainer>{body}</DashboardContainer>;
}
